// /browse — workspace rail + conversation list + message pane.
//
// Pass 2.4: extracted from the monolithic App.tsx so it lives at
// the /browse route.
//
// Pass 2.12: orchestrates the resizable layout. Owns the
// useLayoutState hook (the single source of truth for the
// sidebar + thread-drawer widths) and decides — based on
// useViewportThreshold — whether to render the resize handles
// at all. Below 900px the handles disappear and the panes use
// fixed default widths.
//
// Pass 6.0c.2: URL-as-state. ``activeWorkspaceID`` /
// ``activeConversationID`` derive from ``useParams`` (the route
// is ``/browse/:teamId/:conversationId``); clicks navigate the
// URL rather than calling setters. ``?ts=`` query param seeds
// the message-pane jump-to-ts for shareable permalinks. On a
// hard or soft refresh, the URL fully describes the view —
// React state holds nothing about which workspace / conversation
// is open, so there is no "reset to first workspace on reload"
// regression.

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { AlertCircle, Search as SearchIcon } from "lucide-react";

import { ConversationList } from "../components/ConversationList";
import { useSystemStatus } from "../state/SystemStatusContext";
import { workspaceHealthColor } from "../lib/workspaceHealth";
import { MessagePane } from "../components/MessagePane";
import { ResetLayoutButton } from "../components/ResetLayoutButton";
import { ResizeHandle } from "../components/ResizeHandle";
import { ScopeSelector } from "../components/ScopeSelector";
import { EmptyState } from "../components/ui/EmptyState";
import { SearchBar } from "../components/SearchBar";
import { SearchResults } from "../components/SearchResults";
import { ServiceUnreachableBanner } from "../components/ServiceUnreachableBanner";
import { TopNav } from "../components/TopNav";
import { WorkspaceInfoPanel } from "../components/WorkspaceInfoPanel";
import { WorkspaceSidebar } from "../components/WorkspaceSidebar";
import type { SearchHit } from "../lib/api";
import { useConversations, useUsers, useWorkspaces } from "../lib/hooks";
import { useSearch } from "../lib/useSearch";
import {
  DEFAULTS,
  SIDEBAR_BOUNDS,
  useLayoutState,
} from "../lib/useLayoutState";
import { useViewportThreshold } from "../lib/useViewportThreshold";
import { useAppState } from "../state/AppContext";
import { useAuth } from "../state/AuthContext";
import { EmptyBrowseState } from "../components/EmptyBrowseState";

// Below this viewport the resize handles disappear; the panes
// take the spec defaults. 900px chosen because it's wide enough
// for the workspace rail (64px) + sidebar default (288px) + a
// non-cramped message pane (~500px+) plus the optional thread
// drawer to fit without overflow.
const RESIZABLE_VIEWPORT_THRESHOLD_PX = 900;

export function BrowseView() {
  const navigate = useNavigate();
  const params = useParams<{ teamId?: string; conversationId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setOpenThreadParent } = useAppState();
  const { logout } = useAuth();

  // Pass 6.0c.2: URL is the source of truth. teamIdFromUrl and
  // conversationIdFromUrl may be undefined when the URL is bare
  // /browse — in that case BrowseView falls back to the first
  // workspace (DEFAULT_WORKSPACE_NAVIGATION rule below) without
  // mutating the URL: bare /browse must keep meaning "first
  // workspace" so subsequent refreshes land in the same place.
  const teamIdFromUrl = params.teamId ?? null;
  const conversationIdFromUrl = params.conversationId ?? null;
  const tsFromUrl = searchParams.get("ts");

  const {
    data: workspaces,
    loading: workspacesLoading,
    error: workspacesError,
    refresh: refreshWorkspaces,
  } = useWorkspaces();

  // Resolve the displayed workspace from URL → workspaces list.
  // Three buckets: explicit-and-found, explicit-and-missing (e.g.
  // a stale link to a workspace the user lost access to), and
  // implicit (no :teamId param — pick the first workspace).
  const explicitWorkspace = useMemo(() => {
    if (!teamIdFromUrl || !workspaces) return null;
    return workspaces.find((w) => w.team_id === teamIdFromUrl) ?? null;
  }, [teamIdFromUrl, workspaces]);

  const explicitWorkspaceMissing =
    teamIdFromUrl !== null
    && workspaces !== null
    && explicitWorkspace === null;

  // Implicit-default: first workspace. Used only when no :teamId
  // in URL. Deliberately NOT used as a fallback for the
  // explicit-and-missing case — we want the operator to SEE the
  // banner + click their way to a fresh selection rather than
  // landing on an unrelated workspace.
  const implicitDefaultWorkspace = useMemo(() => {
    if (teamIdFromUrl || !workspaces || workspaces.length === 0) return null;
    return workspaces[0] ?? null;
  }, [teamIdFromUrl, workspaces]);

  const activeWorkspace = explicitWorkspace ?? implicitDefaultWorkspace;

  // Aurora: active workspace health → sidebar header glow dot.

  const { status: sysStatus } = useSystemStatus();

  const activeHealth = (() => {

    if (!activeWorkspace || !sysStatus) return null;

    const row = sysStatus.workspaces.find((w) => w.team_id === activeWorkspace.team_id);

    return row ? workspaceHealthColor({ workspace: row, driveQueue: sysStatus.drive_queue ?? null }) : null;

  })();
  const activeWorkspaceID = activeWorkspace?.team_id ?? null;

  // Pass 6.0c.2: when the URL has /:teamId/:conversationId but the
  // conversationId doesn't exist in this workspace (operator deleted
  // it, or the link is stale, or — most commonly — the user just
  // switched workspaces and the URL still carries the old conv id),
  // strip it silently and show the workspace bare. The
  // ``displayedConversationID`` below is what we PASS DOWN to
  // child components; the URL stays as-is until the operator
  // navigates away (so a typo / temporary loading state doesn't
  // mutate history with a corrected URL the operator didn't type).
  const [infoOpenForID, setInfoOpenForID] = useState<string | null>(null);
  // Pass 2.13 search dropdown open state. Declared here (above the
  // navigate handlers) so S3.8's "dismiss dropdown on navigation"
  // can call setResultsOpen.
  const [resultsOpen, setResultsOpen] = useState(false);

  const navigateToWorkspace = useCallback(
    (teamID: string | null) => {
      setInfoOpenForID(null);
      setResultsOpen(false); // S3.8: dismiss search dropdown on nav
      if (teamID === null) {
        navigate("/browse");
      } else {
        navigate(`/browse/${teamID}`);
      }
    },
    [navigate],
  );

  const navigateToConversation = useCallback(
    (convID: string | null) => {
      if (!activeWorkspaceID) return;
      setResultsOpen(false); // S3.8: dismiss search dropdown on nav (keep query)
      if (convID === null) {
        navigate(`/browse/${activeWorkspaceID}`);
      } else {
        navigate(`/browse/${activeWorkspaceID}/${convID}`);
      }
    },
    [navigate, activeWorkspaceID],
  );

  const onToggleInfo = useCallback((teamID: string) => {
    setInfoOpenForID((cur) => (cur === teamID ? null : teamID));
  }, []);

  const {
    data: conversations,
    loading: conversationsLoading,
    error: conversationsError,
  } = useConversations(activeWorkspaceID);
  const { data: users } = useUsers(activeWorkspaceID);

  // Resolve the displayed conversation from URL → conversations
  // list. ``null`` when the URL doesn't specify one OR when the
  // URL specifies one we can't find in this workspace (the
  // graceful-strip behaviour the brief specifies). Note: we don't
  // navigate() to rewrite the URL in the stripped case — that
  // would create a phantom history entry.
  const activeConversation =
    (conversationIdFromUrl
      && conversations?.find((c) => c.id === conversationIdFromUrl))
    || null;
  const displayedConversationID = activeConversation?.id ?? null;

  // Pass 6.0c.2: ``targetMessageTs`` is hydrated from the ?ts=
  // query param on mount and on URL change. Search-jump clicks
  // navigate(`?ts=…`) so the URL alone carries enough context to
  // restore the same jump-to-ts on refresh.
  const targetMessageTs = tsFromUrl;

  // When the user switches workspace or conversation via the rail
  // / list, drop any open thread drawer — same UX as pre-6.0c.2.
  // Triggered by the URL changing.
  useEffect(() => {
    setOpenThreadParent(null);
  }, [teamIdFromUrl, conversationIdFromUrl, setOpenThreadParent]);

  const layout = useLayoutState();
  const isWide = useViewportThreshold(RESIZABLE_VIEWPORT_THRESHOLD_PX);

  // Pass 2.13: search state. The search bar is mounted in the
  // TopNav center slot whenever a workspace is loaded.
  const search = useSearch({
    activeWorkspaceId: activeWorkspaceID,
    activeConversationId: displayedConversationID,
  });

  // Re-open results dropdown when query changes from empty to non-
  // empty (e.g. URL ?q=… on first load).
  useEffect(() => {
    if (search.query.trim().length > 0) {
      setResultsOpen(true);
    } else {
      setResultsOpen(false);
    }
  }, [search.query]);

  const navigateToHit = useCallback(
    (hit: SearchHit) => {
      // Pass 6.0c.2: a search-hit click is "navigate to
      // /browse/<team>/<conv>?ts=<ts>" — the URL captures every
      // piece of context the message pane needs, so refresh-from-
      // a-search-jump restores the same view.
      //
      // Order matters: set the thread parent BEFORE navigating so
      // the drawer is mounted by the time MessagePane's first
      // render with the new conversation runs. Pre-6.0c.2 the
      // same ordering rule applied for the state-setter path.
      setOpenThreadParent(hit.parent_ts ?? null);
      setResultsOpen(false); // S3.8: close the dropdown on result click
      navigate(
        `/browse/${hit.team_id}/${hit.conversation_id}`
        + `?ts=${encodeURIComponent(hit.ts)}`,
      );
    },
    [navigate, setOpenThreadParent],
  );

  // Below the resizable viewport threshold the search bar collapses
  // to an icon button + the search panel renders below the nav.
  const [narrowSearchOpen, setNarrowSearchOpen] = useState(false);

  // Aurora: the Home command-center search pill links to
  // /browse?focus=search — land with the search input focused so the
  // user can just start typing. The param is stripped (replace) so a
  // refresh doesn't re-steal focus. On narrow viewports the collapsed
  // search overlay is opened first.
  const wantsSearchFocus = searchParams.get("focus") === "search";
  useEffect(() => {
    if (!wantsSearchFocus || !activeWorkspace) return;
    if (!isWide) setNarrowSearchOpen(true);
    const t = window.setTimeout(() => {
      const el = document.querySelector(
        '[data-testid="search-input"]',
      ) as HTMLInputElement | null;
      el?.focus();
      const next = new URLSearchParams(searchParams);
      next.delete("focus");
      setSearchParams(next, { replace: true });
    }, 60);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsSearchFocus, activeWorkspace, isWide]);

  if (workspacesLoading && !workspaces) {
    return (
      <div className="h-screen flex items-center justify-center">
        <p className="font-mono text-sm text-theme-text-tertiary">
          Loading workspaces…
        </p>
      </div>
    );
  }

  if (workspacesError) {
    return (
      <div className="h-screen flex flex-col items-center justify-center gap-3 p-6">
        <p className="font-mono text-sm text-theme-error-text">
          Could not load workspaces: {workspacesError.message}
        </p>
        <button
          type="button"
          onClick={logout}
          className="px-3 py-1.5 border border-theme-border-default font-mono text-[11px] uppercase tracking-widest text-theme-text-secondary hover:text-theme-text-primary"
        >
          Sign out
        </button>
      </div>
    );
  }

  // Empty state — no workspaces ingested yet. Point the user
  // at /upload via the new Chooser path.
  //
  // Phase 6.2: keep the ScopeSelector mounted here too. A superadmin
  // impersonating a user with zero workspaces lands on this state —
  // without the selector they'd be stranded with no way back to
  // "All users".
  if (workspaces && workspaces.length === 0) {
    return (
      <div className="h-screen flex flex-col overflow-hidden">
        <div className="px-6 pt-4 flex justify-end">
          <ScopeSelector />
        </div>
        <div className="flex-1 min-h-0">
          <EmptyBrowseState onLogout={logout} />
        </div>
      </div>
    );
  }

  const searchBarNode = activeWorkspace ? (
    isWide ? (
      <div className="relative w-full max-w-[480px]">
        <SearchBar
          query={search.query}
          onQueryChange={search.setQuery}
          scope={search.scope}
          onScopeChange={search.setScope}
          conversationScopeAvailable={search.conversationScopeAvailable}
          onOpenResults={() => setResultsOpen(true)}
          onCloseResults={() => setResultsOpen(false)}
        />
        <SearchResults
          query={search.query}
          scope={search.scope}
          hits={search.hits}
          totalCount={search.totalCount}
          loading={search.loading}
          error={search.error}
          hasMore={search.hasMore}
          open={resultsOpen}
          showWorkspaceColumn={search.scope === "all"}
          onPivotToAll={() => search.setScope("all")}
          onPickHit={(hit) => navigateToHit(hit)}
          onLoadMore={search.loadMore}
          onClose={() => setResultsOpen(false)}
        />
      </div>
    ) : (
      <button
        type="button"
        onClick={() => setNarrowSearchOpen((s) => !s)}
        data-testid="search-narrow-toggle"
        aria-expanded={narrowSearchOpen}
        className="inline-flex items-center justify-center p-2 border border-theme-border-default text-theme-text-secondary hover:text-theme-text-primary"
      >
        <SearchIcon className="w-4 h-4" strokeWidth={1.5} aria-hidden />
      </button>
    )
  ) : null;

  return (
    <div
      data-testid="browse-view"
      data-active-workspace-id={activeWorkspaceID ?? ""}
      data-active-conversation-id={displayedConversationID ?? ""}
      data-target-ts={targetMessageTs ?? ""}
      className="h-screen flex flex-col overflow-hidden"
    >
      <ServiceUnreachableBanner />
      <TopNav
        rightSlot={
          <>
            <ScopeSelector />
            {isWide && activeWorkspace ? (
              <ResetLayoutButton onClick={layout.resetLayout} />
            ) : null}
          </>
        }
        centerSlot={searchBarNode}
      />
      {/* Pass 6.0c.2: surface the explicit-but-missing case so the
          operator understands the URL silently degraded. Banner
          renders only when the URL named a workspace we couldn't
          find AND we DIDN'T fall back to one (which by design we
          don't — see explicitWorkspaceMissing comment above). */}
      {explicitWorkspaceMissing && (
        <div
          data-testid="browse-workspace-missing-banner"
          className="border-b border-theme-border-default"
        >
          <EmptyState
            size="sm"
            icon={<AlertCircle className="w-full h-full" strokeWidth={1.5} />}
            title="Workspace not accessible"
            description="This workspace isn't in your scope. Pick one from the rail to continue."
          />
        </div>
      )}
      {!isWide && narrowSearchOpen && activeWorkspace && (
        <div
          data-testid="search-narrow-overlay"
          className="relative bg-white/[0.03] backdrop-blur-xl border-b border-white/[0.07] px-3 py-2"
        >
          <div className="relative">
            <SearchBar
              query={search.query}
              onQueryChange={search.setQuery}
              scope={search.scope}
              onScopeChange={search.setScope}
              conversationScopeAvailable={search.conversationScopeAvailable}
              onOpenResults={() => setResultsOpen(true)}
              onCloseResults={() => {
                setResultsOpen(false);
                setNarrowSearchOpen(false);
              }}
            />
            <SearchResults
              query={search.query}
              scope={search.scope}
              hits={search.hits}
              totalCount={search.totalCount}
              loading={search.loading}
              error={search.error}
              hasMore={search.hasMore}
              open={resultsOpen}
              showWorkspaceColumn={search.scope === "all"}
              onPivotToAll={() => search.setScope("all")}
              onPickHit={(hit) => {
                navigateToHit(hit);
                setNarrowSearchOpen(false);
              }}
              onLoadMore={search.loadMore}
              onClose={() => setResultsOpen(false)}
            />
          </div>
        </div>
      )}
      <div className="flex-1 flex overflow-hidden">
        <WorkspaceSidebar
          workspaces={workspaces ?? []}
          activeWorkspaceID={activeWorkspaceID}
          onSelect={navigateToWorkspace}
        />
        {activeWorkspace && infoOpenForID === activeWorkspace.team_id && (
          <WorkspaceInfoPanel
            teamID={activeWorkspace.team_id}
            summary={activeWorkspace}
            onClose={() => setInfoOpenForID(null)}
            onLogout={logout}
            conversationCount={conversations?.length ?? null}
            onDeleted={() => {
              // Pass 2.15: workspace is gone server-side. Refresh
              // the rail so the now-removed entry disappears, drop
              // local active state, navigate to the chooser. The
              // user just made a deliberate decision to delete —
              // landing them on a different workspace would be
              // jarring.
              //
              // Pass 6.0c.2: workspace+conversation state lives in
              // the URL now, so the cleanup is just "navigate
              // away" — no setActiveWorkspace/setActiveConversation
              // calls needed.
              setInfoOpenForID(null);
              setOpenThreadParent(null);
              refreshWorkspaces();
              navigate("/chooser");
            }}
          />
        )}
        {activeWorkspace && (
          <ConversationList
            healthColor={activeHealth}
            workspaceName={activeWorkspace.team_name}
            workspaceTeamID={activeWorkspace.team_id}
            conversations={conversations ?? []}
            activeConversationID={displayedConversationID}
            onSelect={navigateToConversation}
            onToggleInfo={() => onToggleInfo(activeWorkspace.team_id)}
            infoOpen={infoOpenForID === activeWorkspace.team_id}
            loading={conversationsLoading}
            error={conversationsError ? conversationsError.message : null}
            users={users ?? {}}
            // Pass 2.12: pixel-native width. Below the viewport
            // threshold the spec default takes over (no handle is
            // rendered, so the user can't change this anyway).
            width={isWide ? layout.sidebarWidth : DEFAULTS.sidebarWidth}
          />
        )}
        {/*
          Pass 2.12: sidebar↔message resize handle. Renders only
          when the resizable layout is active (viewport ≥ 900px)
          AND there's an active workspace.
        */}
        {isWide && activeWorkspace && (
          <ResizeHandle
            orientation="horizontal"
            onResize={layout.setSidebarWidth}
            getCurrentWidth={() => layout.sidebarWidth}
            minWidth={SIDEBAR_BOUNDS.min}
            maxWidth={SIDEBAR_BOUNDS.max}
            aria-label="Resize conversation list"
            data-testid="resize-handle-sidebar"
          />
        )}
        {activeWorkspace ? (
          <MessagePane
            teamID={activeWorkspace.team_id}
            conversation={activeConversation}
            users={users ?? {}}
            conversations={conversations ?? []}
            threadDrawerWidth={
              isWide ? layout.threadDrawerWidth : DEFAULTS.threadDrawerWidth
            }
            onResizeThreadDrawer={
              isWide ? layout.setThreadDrawerWidth : undefined
            }
            // Pass 6.0c.2: targetMessageTs comes straight from the
            // URL's ?ts= query param. Only meaningful when the
            // conversation is loaded AND matches the URL's
            // :conversationId — guards against a stale ts hanging
            // around after the user navigates to a different
            // conversation that happens to be loading.
            targetMessageTs={
              activeConversation
              && activeConversation.id === conversationIdFromUrl
                ? targetMessageTs
                : null
            }
            // S3.7: drop any ?ts permalink param when jumping to latest.
            onJumpToLatest={
              activeWorkspace && displayedConversationID
                ? () =>
                    navigate(
                      `/browse/${encodeURIComponent(activeWorkspace.team_id)}/${encodeURIComponent(displayedConversationID)}`,
                    )
                : undefined
            }
            // S4.0: "go to parent" reuses the scroll-to-ts machinery
            // by navigating to ?ts=<parentTs>.
            onGoToParent={
              activeWorkspace && displayedConversationID
                ? (parentTs: string) =>
                    navigate(
                      `/browse/${encodeURIComponent(activeWorkspace.team_id)}/${encodeURIComponent(displayedConversationID)}?ts=${encodeURIComponent(parentTs)}`,
                    )
                : undefined
            }
          />
        ) : (
          <main className="flex-1 flex items-center justify-center bg-theme-bg-message-pane">
            <p className="font-mono text-sm text-theme-text-tertiary">
              Select a workspace
            </p>
          </main>
        )}
      </div>
    </div>
  );
}
